import { useState } from 'react';
import { z } from 'zod';

export const COOKIE_PREFERENCES_KEY = 'cookie-preferences';

export const DEFAULT_PREFERENCE = false;

export const cookiePreferencesSchema = z.object({
  acceptAll: z.boolean(),
  settings: z.object({
    analytics: z.boolean(),
    marketing: z.boolean(),
  }),
});

export type CookiePreferences = z.infer<typeof cookiePreferencesSchema>;

type UseCookiePreferencesProps = {
  onAlreadySet?: (preferences: CookiePreferences) => void;
  onSaved?: (preferences: CookiePreferences) => void;
};

export const useCookiePreferences = ({
  onAlreadySet,
  onSaved,
}: UseCookiePreferencesProps = {}) => {
  const [settings, setSettings] = useState<CookiePreferences | null>(() => {
    try {
      const saved = localStorage.getItem(COOKIE_PREFERENCES_KEY);

      if (!saved) {
        return null;
      }

      const parsed = JSON.parse(saved);
      const result = cookiePreferencesSchema.safeParse(parsed);

      if (!result.success) {
        return null;
      }

      onAlreadySet?.(result.data);

      return result.data;
    } catch (error) {
      console.error('Failed to load cookie preferences:', error);

      return null;
    }
  });

  const savePreferences = (newSettings: CookiePreferences) => {
    try {
      localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newSettings));
      setSettings(newSettings);
      onSaved?.(newSettings);
    } catch (error) {
      console.error('Failed to save cookie preferences:', error);
    }
  };

  const updateAnalytics = (
    checked: boolean,
    options: { save?: boolean } = {}
  ) => {
    setSettings((prev) => {
      if (!prev) {
        const newSettings = {
          acceptAll: false,
          settings: {
            analytics: checked,
            marketing: DEFAULT_PREFERENCE,
          },
        };
        newSettings.acceptAll = Object.values(newSettings.settings).every(
          Boolean
        );

        if (options.save) {
          savePreferences(newSettings);
        }

        return newSettings;
      }

      const newSettings = {
        ...prev,
        settings: {
          ...prev.settings,
          analytics: checked,
        },
      };

      newSettings.acceptAll = Object.values(newSettings.settings).every(
        Boolean
      );

      if (options.save) {
        savePreferences(newSettings);
      }

      return newSettings;
    });
  };

  const updateMarketing = (
    checked: boolean,
    options: { save?: boolean } = {}
  ) => {
    setSettings((prev) => {
      if (!prev) {
        const newSettings = {
          acceptAll: false,
          settings: {
            marketing: checked,
            analytics: DEFAULT_PREFERENCE,
          },
        };
        newSettings.acceptAll = Object.values(newSettings.settings).every(
          Boolean
        );

        if (options.save) {
          savePreferences(newSettings);
        }

        return newSettings;
      }

      const newSettings = {
        ...prev,
        settings: {
          ...prev.settings,
          marketing: checked,
        },
      };

      newSettings.acceptAll = Object.values(newSettings.settings).every(
        Boolean
      );

      if (options.save) {
        savePreferences(newSettings);
      }

      return newSettings;
    });
  };

  return {
    settings,
    savePreferences,
    updateAnalytics,
    updateMarketing,
  };
};
